import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import callCenterApi from '@/api/callCenterApi';
import type { ApiResponse } from '@/api/types';

type CallCenterPayload = Record<string, unknown>;
type CallCenterListResponse = ApiResponse<unknown[]>;
type CallCenterItemResponse = ApiResponse<unknown>;

// Query keys for call center-related queries
export const CALL_CENTER_KEYS = {
  clients: ['callCenter', 'clients'] as const,
  clientById: (id: string) => ['callCenter', 'clients', id] as const,
  callbacks: ['callCenter', 'callbacks'] as const,
  callbackById: (id: string) => ['callCenter', 'callbacks', id] as const,
  callHistory: ['callCenter', 'history'] as const,
  callHistoryById: (id: string) => ['callCenter', 'history', id] as const,
};

/**
 * Hook for fetching all clients
 */
export function useClients(
  options: ApiQueryOptions<CallCenterListResponse> = {},
) {
  return useAuthenticatedQuery<CallCenterListResponse>(
    CALL_CENTER_KEYS.clients,
    token => callCenterApi.getClients(token),
    options,
  );
}

/**
 * Hook for fetching a specific client by ID
 */
export function useClient(
  id: string,
  options: ApiQueryOptions<CallCenterListResponse> = {},
) {
  return useAuthenticatedQuery<CallCenterListResponse>(
    CALL_CENTER_KEYS.clientById(id),
    token => callCenterApi.getClientById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for fetching all callback requests
 */
export function useCallbacks(
  options: ApiQueryOptions<CallCenterListResponse> = {},
) {
  return useAuthenticatedQuery<CallCenterListResponse>(
    CALL_CENTER_KEYS.callbacks,
    token => callCenterApi.getCallbacks(token),
    options,
  );
}

/**
 * Hook for fetching a specific callback by ID
 */
export function useCallback(
  id: string,
  options: ApiQueryOptions<CallCenterItemResponse> = {},
) {
  return useAuthenticatedQuery<CallCenterItemResponse>(
    CALL_CENTER_KEYS.callbackById(id),
    token => callCenterApi.getCallbackById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for creating a callback request
 */
export function useCreateCallback(
  options: ApiMutationOptions<
    CallCenterItemResponse,
    { callbackData: CallCenterPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CallCenterItemResponse,
    { callbackData: CallCenterPayload }
  >(
    ({ callbackData, token }) =>
      callCenterApi.createCallback(callbackData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CALL_CENTER_KEYS.callbacks });
      },
      ...options,
    },
  );
}

/**
 * Hook for updating a callback request
 */
export function useUpdateCallback(
  options: ApiMutationOptions<
    CallCenterItemResponse,
    { id: string; callbackData: CallCenterPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CallCenterItemResponse,
    { id: string; callbackData: CallCenterPayload }
  >(
    ({ id, callbackData, token }) =>
      callCenterApi.updateCallback(id, callbackData, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: CALL_CENTER_KEYS.callbackById(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: CALL_CENTER_KEYS.callbacks });
      },
      ...options,
    },
  );
}

/**
 * Hook for deleting a callback request
 */
export function useDeleteCallback(
  options: ApiMutationOptions<ApiResponse<null>, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<ApiResponse<null>, { id: string }>(
    ({ id, token }) => callCenterApi.deleteCallback(id, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.removeQueries({
          queryKey: CALL_CENTER_KEYS.callbackById(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: CALL_CENTER_KEYS.callbacks });
      },
      ...options,
    },
  );
}

/**
 * Hook for fetching call history
 */
export function useCallHistory(
  options: ApiQueryOptions<CallCenterListResponse> = {},
) {
  return useAuthenticatedQuery<CallCenterListResponse>(
    CALL_CENTER_KEYS.callHistory,
    token => callCenterApi.getCallHistory(token),
    options,
  );
}

/**
 * Hook for creating a call history entry
 */
export function useCreateCallHistory(
  options: ApiMutationOptions<
    CallCenterItemResponse,
    { historyData: CallCenterPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CallCenterItemResponse,
    { historyData: CallCenterPayload }
  >(
    ({ historyData, token }) =>
      callCenterApi.createCallHistory(historyData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CALL_CENTER_KEYS.callHistory,
        });
      },
      ...options,
    },
  );
}
